import { useState, useEffect } from 'react';

// True when the key event originates from a place where the user is typing —
// arrow keys must keep their native behavior there (inputs, textareas, the
// interactive hero terminal, anything contentEditable).
function isTypingTarget(el) {
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
}

export function useSectionScroll() {
  const [active, setActive] = useState(0);
  const [total,  setTotal]  = useState(0);

  useEffect(() => {
    const container = document.getElementById('sections-container');
    const sections  = Array.from(document.querySelectorAll('.fullpage-section'));
    if (!container || !sections.length) return;

    setTotal(sections.length);

    // offsetTop is the source of truth — window.innerHeight drifts from the
    // real section height on iOS (dvh vs innerHeight with the address bar).
    const scrollToIndex = (i) => {
      const s = sections[Math.max(0, Math.min(sections.length - 1, i))];
      if (s) container.scrollTo({ top: s.offsetTop, behavior: 'smooth' });
    };

    const nearestIndex = () => {
      let best = 0, bestDist = Infinity;
      sections.forEach((s, i) => {
        const d = Math.abs(s.offsetTop - container.scrollTop);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      return best;
    };

    // Depth transition — while scrolling, each section's content lags
    // behind (parallax), fades and shrinks with distance from the
    // viewport. Pure GPU transforms driven by one rAF per scroll frame.
    // Fullpage pagination (snap + depth) is a desktop pattern; on phones the
    // sections take their natural height and the page scrolls continuously.
    const reduced  = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const fullpage = window.matchMedia('(min-width: 901px)');
    const inners = sections.map((s) => s.querySelector('.s__inner, .hero__inner'));
    let raf = 0;

    const resetDepth = () => inners.forEach((el) => {
      if (el) { el.style.transform = ''; el.style.opacity = ''; }
    });

    const paintDepth = () => {
      raf = 0;
      const st = container.scrollTop;
      const vh = container.clientHeight || 1;
      for (let i = 0; i < sections.length; i++) {
        const el = inners[i];
        if (!el) continue;
        const p = (sections[i].offsetTop - st) / vh; // 0 = aligned, ±1 = one screen away
        const d = Math.abs(p);
        if (d > 1.2) continue; // off-screen, leave as-is
        // Camera push: the incoming section (p>0) lands from slightly
        // zoomed-in, the outgoing one (p<0) recedes while flying off.
        el.style.transform = `translate3d(0, ${(p * 34).toFixed(2)}vh, 0) scale(${(1 + p * 0.055).toFixed(3)})`;
        el.style.opacity = Math.max(0, 1 - d * 1.15).toFixed(3);
      }
    };

    const onScroll = () => {
      if (!reduced && fullpage.matches && !raf) raf = requestAnimationFrame(paintDepth);
    };
    const onLayoutChange = () => { if (fullpage.matches) paintDepth(); else resetDepth(); };
    if (!reduced) {
      container.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      fullpage.addEventListener('change', onLayoutChange);
      onLayoutChange();
    }

    // Reveal a section's content as soon as a slice of it is on screen
    // (sections can be taller than the viewport on phones).
    const revealIO = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { root: container, threshold: 0.12 }
    );
    // The active section is the one crossing the viewport's centre line.
    const activeIO = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(sections.indexOf(e.target)); }),
      { root: container, rootMargin: '-50% 0px -50% 0px', threshold: 0 }
    );
    sections.forEach((s) => { revealIO.observe(s); activeIO.observe(s); });

    // Keyboard → navigate sections (never while the user is typing somewhere)
    const KEYS = ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'PageDown', 'PageUp', 'Home', 'End'];
    const onKey = (e) => {
      if (!KEYS.includes(e.key)) return;
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      if (e.key === 'Home') return scrollToIndex(0);
      if (e.key === 'End')  return scrollToIndex(sections.length - 1);
      const delta = (e.key === 'ArrowDown' || e.key === 'ArrowRight' || e.key === 'PageDown') ? 1 : -1;
      scrollToIndex(nearestIndex() + delta);
    };
    window.addEventListener('keydown', onKey);

    // Nav anchor links → scroll the container
    const onAnchor = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href').slice(1);
      const i  = sections.findIndex((s) => s.id === id);
      if (i !== -1) {
        e.preventDefault();
        scrollToIndex(i);
      }
    };
    document.addEventListener('click', onAnchor);

    // Programmatic navigation (interactive terminal, weather badge, …)
    const onAction = (e) => {
      if (e.detail?.type !== 'goto') return;
      const id = String(e.detail.value || '').replace(/^#/, '');
      const i  = sections.findIndex((s) => s.id === id);
      if (i !== -1) scrollToIndex(i);
    };
    window.addEventListener('portfolio:action', onAction);

    return () => {
      revealIO.disconnect();
      activeIO.disconnect();
      cancelAnimationFrame(raf);
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      fullpage.removeEventListener('change', onLayoutChange);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onAnchor);
      window.removeEventListener('portfolio:action', onAction);
    };
  }, []);

  const goTo = (i) => {
    const container = document.getElementById('sections-container');
    const sections  = Array.from(document.querySelectorAll('.fullpage-section'));
    const s = sections[i];
    if (container && s) container.scrollTo({ top: s.offsetTop, behavior: 'smooth' });
  };

  return { active, total, goTo };
}
